import { Link, usePage } from '@inertiajs/react'
import {
  LayoutDashboard,
  AppWindow,
  BarChart3,
  Settings,
  BookOpen,
  ExternalLink,
  Webhook,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoIcon } from '@/components/landing/Logo'

const navItems = [
  { href: '/dashboard', label: 'Overview', icon: LayoutDashboard },
  { href: '/dashboard/applications', label: 'Applications', icon: AppWindow },
  { href: '/dashboard/webhooks', label: 'Webhooks', icon: Webhook },
  { href: '/dashboard/usage', label: 'Usage', icon: BarChart3 },
  { href: '/dashboard/settings', label: 'Settings', icon: Settings },
]

interface SidebarProps {
  orgName: string
}

export function Sidebar({ orgName }: SidebarProps) {
  const { url } = usePage()

  const linkClass = (isActive: boolean) =>
    cn(
      'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all',
      isActive
        ? 'text-foreground'
        : 'text-muted-foreground hover:text-foreground',
    )

  return (
    <div className="flex h-full flex-col gap-2 p-4">
      {/* Brand row */}
      <Link
        href="/"
        className="flex items-center gap-2 px-2 py-3 font-heading text-[15px] font-semibold tracking-tight"
      >
        <LogoIcon />
        <span>KonexZero</span>
      </Link>

      {/* Org card — surface treatment, not a border */}
      <div className="surface-card mx-1 mt-1 mb-3 px-3 py-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/80">
          Organization
        </div>
        <div className="mt-1 truncate font-heading text-sm font-semibold">
          {orgName}
        </div>
      </div>

      {/* Primary nav */}
      <nav className="flex flex-col gap-1">
        {navItems.map(({ href, label, icon: Icon }) => {
          const isActive =
            href === '/dashboard' ? url === '/dashboard' : url.startsWith(href)
          return (
            <Link key={href} href={href} className={linkClass(isActive)}>
              {/* Active indicator — gradient bar instead of solid bg */}
              {isActive && (
                <span
                  aria-hidden
                  className="absolute inset-y-1 left-0 w-[3px] rounded-full"
                  style={{
                    background:
                      'linear-gradient(180deg, hsl(var(--brand-1)) 0%, hsl(var(--brand-2)) 100%)',
                  }}
                />
              )}
              {/* Active soft surface */}
              {isActive && (
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-lg bg-foreground/[0.04]"
                />
              )}
              <Icon
                className={cn(
                  'relative h-4 w-4 shrink-0 transition-colors',
                  isActive ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground',
                )}
                strokeWidth={1.7}
              />
              <span className="relative">{label}</span>
            </Link>
          )
        })}
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Docs CTA — surface card with soft hover, not a separator */}
      <Link
        href="/docs"
        className="surface-card surface-card-hover group relative mt-2 flex flex-col gap-1 p-4"
      >
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-foreground/80" strokeWidth={1.7} />
          <span className="font-heading text-sm font-semibold">API Docs</span>
          <ExternalLink className="ml-auto h-3 w-3 text-muted-foreground/70 transition group-hover:text-foreground" />
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Curl-able reference, live explorer, and OpenAPI spec.
        </p>
      </Link>
    </div>
  )
}
